import React, { useState, useEffect } from 'react';
import { PageHeader } from '../../components/ui';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import { Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import { shopsService, squarePaymentsService, taxTypesService } from '../../services/api';
import { useShop } from '../../contexts/ShopContext';
import { motion } from 'motion/react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import safeStorage from '../../utils/safeStorage';
import { SquareConnectionStatus, SquareLocation, TaxType, UserRole } from '../../types';
import { DayOfWeek } from '../../types';
import { formatClockTime } from '../../utils/shopTime';
import ClockTimeField from '../../components/ClockTimeField';

// Time slot sizes for dropdown
const timeSlotOptions = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
];

// Days of the week
const daysOfWeek = [
  'MONDAY',
  'TUESDAY', 
  'WEDNESDAY', 
  'THURSDAY', 
  'FRIDAY', 
  'SATURDAY', 
  'SUNDAY'
];

interface BusinessHour {
  open: string | null;
  close: string | null;
}

interface Holiday {
  date: string;
  description: string;
}

interface SpecialHours {
  date: string;
  open: string | null;
  close: string | null;
  description: string;
}

interface ShopSettings {
  id: string;
  shopId: string;
  timeSlotSize: number;
  maxConcurrentBookings: number;
  /** Service bays on the workshop floor; 0 = not set up. */
  bayCount?: number;
  timezone: string;
  businessHours: Record<string, BusinessHour>;
  holidays: Holiday[];
  specialHours: SpecialHours[];
}

/** Largest rate the backing DECIMAL(5,4) column can hold, as a percentage. */
const MAX_TAX_PERCENT = 999.99;

/** 0.0825 → "8.25". Trailing zeroes trimmed so the field reads cleanly. */
const fractionToPercentInput = (value: number | string | null | undefined) => {
  const fraction = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(fraction) || fraction === 0) return '0';
  return String(Number((fraction * 100).toFixed(2)));
};

// List of common US timezones for dropdown
const timezoneOptions = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
];

const ShopSettingsPage: React.FC = () => {
  const { selectedShop } = useShop();
  const { isAuthenticated, isAdmin, isSuperAdmin, user } = useAuth();
  const navigate = useNavigate();
  
  const [settings, setSettings] = useState<ShopSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);

  // Tax types. Rates are edited as percentages and converted on save.
  const [taxTypes, setTaxTypes] = useState<TaxType[]>([]);
  const [taxDraft, setTaxDraft] = useState<{
    id: string | null;
    name: string;
    components: { name: string; percent: string }[];
  } | null>(null);
  
  // Holiday form states
  const [holidayDate, setHolidayDate] = useState<Date | null>(null);
  const [holidayDescription, setHolidayDescription] = useState('');
  
  // Special hours form states
  const [specialDate, setSpecialDate] = useState<Date | null>(null);
  const [specialOpenTime, setSpecialOpenTime] = useState('');
  const [specialCloseTime, setSpecialCloseTime] = useState('');
  const [specialDescription, setSpecialDescription] = useState('');

  // Square connection (Payments tab)
  const [searchParams, setSearchParams] = useSearchParams();
  const [squareStatus, setSquareStatus] = useState<SquareConnectionStatus | null>(null);
  const [squareLocations, setSquareLocations] = useState<SquareLocation[]>([]);
  const [squareDeviceId, setSquareDeviceId] = useState('');
  const [squareBusy, setSquareBusy] = useState(false);
  
  // Debug user authentication status and permissions
  useEffect(() => {
    console.log('Auth Debug - User:', user);
    console.log('Auth Debug - isAuthenticated:', isAuthenticated);
    console.log('Auth Debug - isAdmin:', isAdmin);
    console.log('Auth Debug - isSuperAdmin:', isSuperAdmin);
    console.log('Auth Debug - User Role:', user?.role);
    console.log('Auth Debug - Token:', safeStorage.getItem('token'));
  }, [user, isAuthenticated, isAdmin, isSuperAdmin]);
  
  // Check for permissions to access this page
  useEffect(() => {
    if (!isAuthenticated) {
      console.log('Not authenticated, redirecting to login');
      setError('You must be logged in to access shop settings');
      // Redirect to login after a short delay to show the error
      setTimeout(() => navigate('/login'), 2000);
      return;
    }
    
    // Check if user has appropriate role (ADMIN or SUPER_ADMIN)
    const hasAdminAccess = isAdmin || isSuperAdmin || (user?.role === UserRole.ADMIN) || (user?.role === UserRole.SUPER_ADMIN);
    
    if (!hasAdminAccess) {
      console.log('User does not have admin privileges, redirecting');
      console.log('Current user role:', user?.role);
      setError('You do not have permission to access shop settings');
      // Redirect to dashboard after showing error
      setTimeout(() => navigate('/dashboard'), 2000);
      return;
    }
    
    console.log('User has admin access, proceeding with page load');
  }, [isAuthenticated, isAdmin, isSuperAdmin, user, navigate]);
  
  // Load shop settings
  useEffect(() => {
    const fetchSettings = async () => {
      if (!selectedShop) return;
      
      // Check if user has appropriate role (ADMIN or SUPER_ADMIN)
      const hasAdminAccess = isAdmin || isSuperAdmin || (user?.role === UserRole.ADMIN) || (user?.role === UserRole.SUPER_ADMIN);
      
      // Don't try to fetch if not authenticated or missing permissions
      if (!isAuthenticated || !hasAdminAccess) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Log the request for debugging purposes
        console.log(`Fetching shop settings for shop ID: ${selectedShop.id}`);
        const token = safeStorage.getItem('token');
        console.log(`Current auth token exists: ${!!token}`);
        if (token) {
          console.log(`Token prefix: ${token.substring(0, 15)}...`);
          
          try {
            // Check if token can be decoded
            const tokenParts = token.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              console.log('Token payload:', payload);
              console.log('Token expiration:', new Date(payload.exp * 1000).toISOString());
              console.log('Current time:', new Date().toISOString());
              console.log('Token expired:', payload.exp * 1000 < Date.now());
            }
          } catch (e) {
            console.error('Error decoding token:', e);
          }
        }
        
        // Add a short delay to ensure logs are visible
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Fetch shop settings from the API
        const response = await shopsService.getShopSettings(selectedShop.id);
        
        // Ensure maxConcurrentBookings is never undefined
        if (response.maxConcurrentBookings === undefined) {
          console.log('Setting default maxConcurrentBookings value of 1 for missing field');
          response.maxConcurrentBookings = 1;
        } else {
          console.log('Received maxConcurrentBookings:', response.maxConcurrentBookings);
        }
        
        setSettings(response);
      } catch (err: any) {
        console.error('Failed to load shop settings:', err);
        
        // Handle authentication errors specifically
        if (err.response?.status === 401 || err.response?.status === 403) {
          console.log('Auth error response:', err.response);
          console.log('Current user role:', user?.role);
          
          const token = safeStorage.getItem('token');
          if (token) {
            console.log('Auth token present but access denied - likely a backend permission issue');
            
            // If we're getting a 403 but we seem to be authenticated and have admin privileges,
            // show an appropriate error but let the user continue using the app
            if (err.response?.status === 403 && hasAdminAccess) {
              setError('API permission issue detected. You appear to be logged in as admin, but the server denied access. Some functionality may be limited.');
              
              // Create default settings so the UI still works
              setSettings({
                id: `mock-${selectedShop.id}`,
                shopId: selectedShop.id,
                timeSlotSize: 45,
                maxConcurrentBookings: 1,
                timezone: 'America/New_York',
                businessHours: {
                  [DayOfWeek.MONDAY]: { open: '09:00:00', close: '17:00:00' },
                  [DayOfWeek.TUESDAY]: { open: '09:00:00', close: '17:00:00' },
                  [DayOfWeek.WEDNESDAY]: { open: '09:00:00', close: '17:00:00' },
                  [DayOfWeek.THURSDAY]: { open: '09:00:00', close: '17:00:00' },
                  [DayOfWeek.FRIDAY]: { open: '09:00:00', close: '17:00:00' },
                  [DayOfWeek.SATURDAY]: { open: '10:00:00', close: '15:00:00' },
                  [DayOfWeek.SUNDAY]: { open: null, close: null }
                },
                holidays: [],
                specialHours: []
              } as ShopSettings);
              
              // Exit early to prevent the normal error handling
              return;
            }
          } else {
            console.log('No auth token present - user likely not logged in');
          }
          
          let errorMessage = 'You are not authorized to access shop settings.';
          
          if (err.response?.status === 403) {
            errorMessage += ' This may be an API permission issue. Please ensure your account has the correct role.';
          } else {
            errorMessage += ' Please log in with admin privileges.';
          }
          
          setError(errorMessage);
          
          // If unauthorized, redirect to login
          if (err.response?.status === 401) {
            setTimeout(() => navigate('/login'), 2000);
          }
        } else {
          setError(err.response?.data?.message || 'Failed to load shop settings');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, [selectedShop, isAuthenticated, isAdmin, isSuperAdmin, user, navigate]);
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };
  
  const loadTaxTypes = async () => {
    try {
      setTaxTypes(await taxTypesService.getTaxTypes());
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load tax types');
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !selectedShop) return;
    loadTaxTypes();
     
  }, [isAuthenticated, selectedShop]);

  // --- Square connection (Payments tab) ---

  const loadSquareStatus = async () => {
    if (!selectedShop) return;
    try {
      const status = await squarePaymentsService.getStatus(selectedShop.id);
      setSquareStatus(status);
      setSquareDeviceId(status.deviceId || '');
      if (status.connected) {
        setSquareLocations(await squarePaymentsService.getLocations(selectedShop.id));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load Square connection status');
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !selectedShop) return;
    loadSquareStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, selectedShop]);

  // Landing back from the Square OAuth redirect: surface the result once,
  // open the Payments tab, then scrub the query params from the URL.
  useEffect(() => {
    const result = searchParams.get('square');
    if (!result) return;
    if (result === 'connected') {
      setSuccessMessage('Square account connected');
    } else {
      setError(`Square connection failed (${searchParams.get('reason') || 'unknown'})`);
    }
    setSelectedTab(4);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSquareConnect = async () => {
    if (!selectedShop) return;
    try {
      setSquareBusy(true);
      const url = await squarePaymentsService.getConnectUrl(selectedShop.id);
      window.location.href = url; // hand the browser to Square
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not start the Square connection');
      setSquareBusy(false);
    }
  };

  const handleSquareDisconnect = async () => {
    if (!selectedShop) return;
    if (!window.confirm('Disconnect Square? Online and terminal payments will stop working for this shop.')) return;
    try {
      setSquareBusy(true);
      await squarePaymentsService.disconnect(selectedShop.id);
      setSquareStatus({ connected: false });
      setSquareLocations([]);
      setSuccessMessage('Square disconnected');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to disconnect Square');
    } finally {
      setSquareBusy(false);
    }
  };

  const handleSquareLocationSave = async (locationId: string) => {
    if (!selectedShop || !locationId) return;
    try {
      setSquareBusy(true);
      const status = await squarePaymentsService.setLocation(
        { locationId, deviceId: squareDeviceId.trim() },
        selectedShop.id,
      );
      setSquareStatus(status);
      setSuccessMessage('Square location saved');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save the Square location');
    } finally {
      setSquareBusy(false);
    }
  };

  const openTaxEditor = (taxType?: TaxType) => {
    setTaxDraft({
      id: taxType?.id ?? null,
      name: taxType?.name ?? '',
      components: (taxType?.components ?? []).map((component) => ({
        name: component.name,
        percent: fractionToPercentInput(component.rate),
      })),
    });
  };

  const handleSaveTaxType = async () => {
    if (!taxDraft) return;

    if (!taxDraft.name.trim()) {
      setError('The tax type needs a name');
      return;
    }

    // Percent in the field, fraction on the wire. Rounded to 4dp to match the
    // DECIMAL(5,4) column, so 8.255% is not silently truncated by MySQL.
    const components = [];
    for (const component of taxDraft.components) {
      if (!component.name.trim()) {
        setError('Every tax component needs a name');
        return;
      }
      const percent = Number(component.percent);
      if (!Number.isFinite(percent) || percent < 0) {
        setError('Tax rates must be zero or a positive percentage');
        return;
      }
      if (percent > MAX_TAX_PERCENT) {
        setError(`Tax rates cannot exceed ${MAX_TAX_PERCENT}%`);
        return;
      }
      components.push({
        name: component.name.trim(),
        rate: Number((percent / 100).toFixed(4)),
      });
    }

    try {
      setError(null);
      if (taxDraft.id) {
        await taxTypesService.updateTaxType(taxDraft.id, {
          name: taxDraft.name.trim(),
          components,
        });
      } else {
        await taxTypesService.createTaxType({ name: taxDraft.name.trim(), components });
      }
      setTaxDraft(null);
      await loadTaxTypes();
      setSuccessMessage('Tax type saved');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save the tax type');
    }
  };

  const runTaxAction = async (action: () => Promise<unknown>, message: string) => {
    try {
      setError(null);
      await action();
      await loadTaxTypes();
      setSuccessMessage(message);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Something went wrong');
    }
  };

  // Save general settings
  const handleSaveGeneralSettings = async () => {
    if (!settings || !selectedShop) return;
    
    try {
      setError(null);

      // Log the values being sent to ensure maxConcurrentBookings is included
      const updateData = {
        timeSlotSize: settings.timeSlotSize,
        maxConcurrentBookings: settings.maxConcurrentBookings || 1, // Ensure a default if undefined
        bayCount: settings.bayCount ?? 0,
        timezone: settings.timezone || 'America/Chicago', // Default to CST if undefined
        businessHours: settings.businessHours
      };
      
      console.log('Saving shop settings with data:', JSON.stringify(updateData, null, 2));
      
      await shopsService.updateShopSettings(settings.id, updateData);
      
      // Verify the settings were saved by refreshing from the server
      const refreshedSettings = await shopsService.getShopSettings(selectedShop.id);
      console.log('Refreshed settings from server:', JSON.stringify(refreshedSettings, null, 2));
      console.log('Refreshed maxConcurrentBookings:', refreshedSettings.maxConcurrentBookings, 'type:', typeof refreshedSettings.maxConcurrentBookings);
      
      // Ensure maxConcurrentBookings exists in the refreshed settings
      if (refreshedSettings.maxConcurrentBookings === undefined) {
        console.log('Adding missing maxConcurrentBookings to refreshed settings');
        refreshedSettings.maxConcurrentBookings = updateData.maxConcurrentBookings;
      }
      
      setSettings(refreshedSettings);

      setSuccessMessage('General settings saved successfully');
      console.log('Settings saved successfully:', refreshedSettings);
    } catch (err: any) {
      console.error('Failed to save general settings:', err);
      
      // Handle authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        const errorMessage = 'You are not authorized to modify shop settings. Please log in with admin privileges.';
        setError(errorMessage);
        
        // If unauthorized, redirect to login
        if (err.response?.status === 401) {
          setTimeout(() => navigate('/login'), 2000);
        }
      } else {
        setError(err.response?.data?.message || 'Failed to save general settings');
      }
    }
  };
  
  // Add a holiday with improved error handling
  const handleAddHoliday = async () => {
    if (!settings || !selectedShop || !holidayDate) return;
    
    try {
      setError(null);
      
      const newHoliday: Holiday = {
        date: format(holidayDate, 'yyyy-MM-dd'),
        description: holidayDescription
      };
      
      const updatedHolidays = [...settings.holidays, newHoliday];
      
      await shopsService.updateShopSettings(settings.id, {
        holidays: updatedHolidays
      });
      
      setSettings({
        ...settings,
        holidays: updatedHolidays
      });
      
      setSuccessMessage('Holiday added successfully');
      
      // Reset form
      setHolidayDate(null);
      setHolidayDescription('');
    } catch (err: any) {
      console.error('Failed to add holiday:', err);
      
      // Handle authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('You are not authorized to modify shop holidays. Please log in with admin privileges.');
      } else {
        setError(err.response?.data?.message || 'Failed to add holiday');
      }
    }
  };
  
  // Delete a holiday with improved error handling
  const handleDeleteHoliday = async (index: number) => {
    if (!settings || !selectedShop) return;
    
    try {
      setError(null);
      
      const updatedHolidays = [...settings.holidays];
      updatedHolidays.splice(index, 1);
      
      await shopsService.updateShopSettings(settings.id, {
        holidays: updatedHolidays
      });
      
      setSettings({
        ...settings,
        holidays: updatedHolidays
      });
      
      setSuccessMessage('Holiday removed successfully');
    } catch (err: any) {
      console.error('Failed to remove holiday:', err);
      
      // Handle authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('You are not authorized to modify shop holidays. Please log in with admin privileges.');
      } else {
        setError(err.response?.data?.message || 'Failed to remove holiday');
      }
    }
  };
  
  // Add special hours with improved error handling
  const handleAddSpecialHours = async () => {
    if (!settings || !selectedShop || !specialDate) return;
    
    try {
      setError(null);
      
      const newSpecialHours: SpecialHours = {
        date: format(specialDate, 'yyyy-MM-dd'),
        open: specialOpenTime || null,
        close: specialCloseTime || null,
        description: specialDescription
      };
      
      const updatedSpecialHours = [...settings.specialHours, newSpecialHours];
      
      await shopsService.updateShopSettings(settings.id, {
        specialHours: updatedSpecialHours
      });
      
      setSettings({
        ...settings,
        specialHours: updatedSpecialHours
      });
      
      setSuccessMessage('Special hours added successfully');
      
      // Reset form
      setSpecialDate(null);
      setSpecialOpenTime('');
      setSpecialCloseTime('');
      setSpecialDescription('');
    } catch (err: any) {
      console.error('Failed to add special hours:', err);
      
      // Handle authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('You are not authorized to modify shop special hours. Please log in with admin privileges.');
      } else {
        setError(err.response?.data?.message || 'Failed to add special hours');
      }
    }
  };
  
  // Delete special hours with improved error handling
  const handleDeleteSpecialHours = async (index: number) => {
    if (!settings || !selectedShop) return;
    
    try {
      setError(null);
      
      const updatedSpecialHours = [...settings.specialHours];
      updatedSpecialHours.splice(index, 1);
      
      await shopsService.updateShopSettings(settings.id, {
        specialHours: updatedSpecialHours
      });
      
      setSettings({
        ...settings,
        specialHours: updatedSpecialHours
      });
      
      setSuccessMessage('Special hours removed successfully');
    } catch (err: any) {
      console.error('Failed to remove special hours:', err);
      
      // Handle authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('You are not authorized to modify shop special hours. Please log in with admin privileges.');
      } else {
        setError(err.response?.data?.message || 'Failed to remove special hours');
      }
    }
  };
  
  // Update business hours for a day
  const handleBusinessHourChange = (
    day: string, 
    field: 'open' | 'close', 
    value: string | null
  ) => {
    if (!settings) return;
    
    setSettings({
      ...settings,
      businessHours: {
        ...settings.businessHours,
        [day]: {
          ...settings.businessHours[day],
          [field]: value
        }
      }
    });
  };
  
  // Add a new function to mark a day as closed by clearing both open and close times
  const handleMarkDayClosed = (day: string) => {
    if (!settings) return;
    
    setSettings({
      ...settings,
      businessHours: {
        ...settings.businessHours,
        [day]: {
          open: null,
          close: null
        }
      }
    });
  };
  
  if (loading) {
    return (
      <Container>
        <Typography variant="h4">Loading shop settings...</Typography>
      </Container>
    );
  }
  
  if (!selectedShop) {
    return (
      <Container>
        <Alert severity="warning">
          Please select a shop first to manage its settings.
        </Alert>
      </Container>
    );
  }
  
  // Check if user has appropriate role (ADMIN or SUPER_ADMIN)
  const hasAdminAccess = isAdmin || isSuperAdmin || (user?.role === UserRole.ADMIN) || (user?.role === UserRole.SUPER_ADMIN);
  
  // Show a permission denied message if not authenticated or lacking permissions
  if (!isAuthenticated || !hasAdminAccess) {
    return (
      <Container>
        <Alert severity="error">
          You don't have permission to access shop settings.
          {!isAuthenticated && " Please log in first."}
          {isAuthenticated && !hasAdminAccess && (
            <Box mt={2}>
              <Typography variant="body2">
                Current role: {user?.role || "Unknown"}
              </Typography>
              <Typography variant="body2">
                Required role: ADMIN or SUPER_ADMIN
              </Typography>
            </Box>
          )}
        </Alert>
      </Container>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <PageHeader
          title="Shop settings"
          subtitle="Business hours, booking capacity, holidays, and timezone."
        />

        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            aria-label="shop settings tabs"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="General Settings" />
            <Tab label="Taxes" />
            <Tab label="Holidays" />
            <Tab label="Special Hours" />
            <Tab label="Payments" />
          </Tabs>
          
          {/* General Settings Tab */}
          {selectedTab === 0 && settings && (
            <Box p={3}>
              <Typography variant="h6" gutterBottom>
                Appointment Time Slots
              </Typography>
              
              <Box mb={4}>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="time-slot-size-label">Time Slot Size</InputLabel>
                  <Select
                    labelId="time-slot-size-label"
                    value={settings.timeSlotSize}
                    label="Time Slot Size"
                    onChange={(e) => setSettings({
                      ...settings,
                      timeSlotSize: Number(e.target.value)
                    })}
                  >
                    {timeSlotOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="timezone-label">Shop Timezone</InputLabel>
                  <Select
                    labelId="timezone-label"
                    value={settings.timezone || 'America/Chicago'}
                    label="Shop Timezone"
                    onChange={(e) => setSettings({
                      ...settings,
                      timezone: e.target.value
                    })}
                  >
                    {timezoneOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    All appointment times will be shown in this timezone. Default: Central Time (CT)
                  </Typography>
                </FormControl>
                
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="max-concurrent-bookings-label">Maximum Concurrent Bookings</InputLabel>
                  <Select
                    labelId="max-concurrent-bookings-label"
                    value={settings.maxConcurrentBookings || 1}
                    label="Maximum Concurrent Bookings"
                    onChange={(e) => {
                      const newValue = Number(e.target.value);
                      console.log('Setting maxConcurrentBookings to:', newValue);
                      setSettings({
                        ...settings,
                        maxConcurrentBookings: newValue
                      });
                    }}
                  >
                    {[1, 2, 3, 4, 5, 6, 8, 10].map(value => (
                      <MenuItem key={value} value={value}>
                        {value} {value === 1 ? 'booking' : 'bookings'} per time slot
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Maximum number of appointments that can be booked for the same time slot.
                  When this limit is reached, the time slot will be shown as unavailable.
                </Typography>

                <TextField
                  type="number"
                  label="Service bays"
                  value={settings.bayCount ?? 0}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      bayCount: Math.min(99, Math.max(0, Math.floor(Number(e.target.value) || 0))),
                    })
                  }
                  slotProps={{ htmlInput: { min: 0, max: 99, step: 1 } }}
                  helperText="How many bays the workshop runs. The dashboard shows one card per bay; 0 hides the floor until you set it."
                  sx={{ mb: 2, maxWidth: 360 }}
                  fullWidth
                />
              </Box>

              <Typography variant="h6" gutterBottom>
                Business Hours
              </Typography>
              
              <Stack spacing={3}>
                {daysOfWeek.map(day => (
                  <Card key={day} sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {day.charAt(0) + day.slice(1).toLowerCase()}
                      </Typography>
                      
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <Box width={{ xs: '100%', sm: '45%' }}>
                          <ClockTimeField
                            label="Opening Time"
                            value={settings.businessHours[day]?.open}
                            onChange={(time) => handleBusinessHourChange(day, 'open', time)}
                          />
                        </Box>
                        
                        <Box width={{ xs: '100%', sm: '45%' }}>
                          <ClockTimeField
                            label="Closing Time"
                            value={settings.businessHours[day]?.close}
                            onChange={(time) => handleBusinessHourChange(day, 'close', time)}
                          />
                        </Box>
                        
                        <Box 
                          width={{ xs: '100%', sm: '10%' }} 
                          display="flex" 
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Button
                            color="error"
                            onClick={() => handleMarkDayClosed(day)}
                            disabled={!settings.businessHours[day]?.open && !settings.businessHours[day]?.close}
                          >
                            Mark Closed
                          </Button>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveGeneralSettings}
                >
                  Save General Settings
                </Button>
              </Box>
            </Box>
          )}
          
          {/* Taxes Tab */}
          {selectedTab === 1 && (
            <Box p={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Tax Types</Typography>
                <Button startIcon={<AddIcon />} onClick={() => openTaxEditor()}>
                  New tax type
                </Button>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Each invoice line is priced by a tax type. A type holds any number of named
                components — Federal, State, County — which are added together and shown separately
                on the invoice. Rates above 100% are allowed, since stacked import duty and excise
                often exceed it. Rates are copied onto an invoice when it is generated, so editing a
                type here never rewrites an invoice that has already been issued.
              </Typography>

              {taxTypes.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No tax types yet. The first one you create becomes the default for new invoice lines.
                </Typography>
              )}

              <Stack spacing={2}>
                {taxTypes.map((taxType) => (
                  <Card key={taxType.id} sx={{ borderRadius: 2, opacity: taxType.isActive ? 1 : 0.6 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {taxType.name}
                            {taxType.isDefault && ' · default'}
                            {!taxType.isActive && ' · retired'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {(taxType.components ?? []).length === 0
                              ? 'No components — this type charges no tax'
                              : (taxType.components ?? [])
                                  .map(
                                    (component) =>
                                      `${component.name} ${fractionToPercentInput(component.rate)}%`,
                                  )
                                  .join('  ·  ')}
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={1}>
                          <Button size="small" onClick={() => openTaxEditor(taxType)}>
                            Edit
                          </Button>
                          {!taxType.isDefault && taxType.isActive && (
                            <Button
                              size="small"
                              onClick={() =>
                                runTaxAction(
                                  () => taxTypesService.setDefaultTaxType(taxType.id),
                                  `"${taxType.name}" is now the default`,
                                )
                              }
                            >
                              Make default
                            </Button>
                          )}
                          {!taxType.isDefault && (
                            <Button
                              size="small"
                              onClick={() =>
                                runTaxAction(
                                  () =>
                                    taxTypesService.updateTaxType(taxType.id, {
                                      isActive: !taxType.isActive,
                                    }),
                                  taxType.isActive ? 'Tax type retired' : 'Tax type restored',
                                )
                              }
                            >
                              {taxType.isActive ? 'Retire' : 'Restore'}
                            </Button>
                          )}
                          {!taxType.isDefault && (
                            <IconButton
                              aria-label="Delete tax type"
                              onClick={() =>
                                runTaxAction(
                                  () => taxTypesService.deleteTaxType(taxType.id),
                                  'Tax type deleted',
                                )
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>

              {taxDraft && (
                <Card sx={{ borderRadius: 2, mt: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {taxDraft.id ? 'Edit tax type' : 'New tax type'}
                    </Typography>

                    <TextField
                      label="Name"
                      fullWidth
                      value={taxDraft.name}
                      onChange={(e) => setTaxDraft({ ...taxDraft, name: e.target.value })}
                      sx={{ mb: 2, maxWidth: 360 }}
                      placeholder="Parts, Labor (exempt), Imported…"
                    />

                    <Stack spacing={1.5}>
                      {taxDraft.components.map((component, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            label="Component"
                            value={component.name}
                            placeholder="Federal, State, County…"
                            onChange={(e) =>
                              setTaxDraft({
                                ...taxDraft,
                                components: taxDraft.components.map((item, i) =>
                                  i === index ? { ...item, name: e.target.value } : item,
                                ),
                              })
                            }
                          />
                          <TextField
                            size="small"
                            label="Rate"
                            type="number"
                            value={component.percent}
                            onChange={(e) =>
                              setTaxDraft({
                                ...taxDraft,
                                components: taxDraft.components.map((item, i) =>
                                  i === index ? { ...item, percent: e.target.value } : item,
                                ),
                              })
                            }
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                            inputProps={{ min: 0, max: MAX_TAX_PERCENT, step: 0.25 }}
                            sx={{ width: 160 }}
                          />
                          <IconButton
                            aria-label="Remove component"
                            onClick={() =>
                              setTaxDraft({
                                ...taxDraft,
                                components: taxDraft.components.filter((_, i) => i !== index),
                              })
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>

                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      sx={{ mt: 1 }}
                      onClick={() =>
                        setTaxDraft({
                          ...taxDraft,
                          components: [...taxDraft.components, { name: '', percent: '0' }],
                        })
                      }
                    >
                      Add component
                    </Button>

                    <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                      <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveTaxType}>
                        Save tax type
                      </Button>
                      <Button onClick={() => setTaxDraft(null)}>Cancel</Button>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}

          {/* Holidays Tab */}
          {selectedTab === 2 && settings && (
            <Box p={3}>
              <Typography variant="h6" gutterBottom>
                Add Holiday
              </Typography>
              
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
                  <Box width={{ xs: '100%', sm: '30%' }}>
                    <DatePicker
                      label="Holiday Date"
                      value={holidayDate}
                      onChange={(date) => setHolidayDate(date)}
                      slotProps={{ 
                        textField: { 
                          fullWidth: true,
                          helperText: 'Shop will be closed on this date' 
                        } 
                      }}
                    />
                  </Box>
                  
                  <Box width={{ xs: '100%', sm: '50%' }}>
                    <TextField
                      label="Description"
                      value={holidayDescription}
                      onChange={(e) => setHolidayDescription(e.target.value)}
                      fullWidth
                      placeholder="Holiday description (e.g. Christmas Day)"
                    />
                  </Box>
                  
                  <Box width={{ xs: '100%', sm: '20%' }} display="flex" alignItems="flex-start">
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleAddHoliday}
                      disabled={!holidayDate || !holidayDescription}
                      fullWidth
                      sx={{ height: '56px' }}
                    >
                      Add
                    </Button>
                  </Box>
                </Stack>
              </LocalizationProvider>
              
              <Typography variant="h6" gutterBottom>
                Holidays
              </Typography>
              
              {settings.holidays.length === 0 ? (
                <Alert severity="info">
                  No holidays have been added yet.
                </Alert>
              ) : (
                <List>
                  {settings.holidays.map((holiday, index) => (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={holiday.description}
                        secondary={format(parseISO(holiday.date), 'MMMM d, yyyy')}
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          aria-label="delete"
                          onClick={() => handleDeleteHoliday(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
          
          {/* Special Hours Tab */}
          {selectedTab === 3 && settings && (
            <Box p={3}>
              <Typography variant="h6" gutterBottom>
                Add Special Hours
              </Typography>
              
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Stack spacing={2} sx={{ mb: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Box width={{ xs: '100%', sm: '30%' }}>
                      <DatePicker
                        label="Date"
                        value={specialDate}
                        onChange={(date) => setSpecialDate(date)}
                        slotProps={{ 
                          textField: { 
                            fullWidth: true,
                            helperText: 'Date for special shop hours' 
                          } 
                        }}
                      />
                    </Box>
                    
                    <Box width={{ xs: '100%', sm: '70%' }}>
                      <TextField
                        label="Description"
                        value={specialDescription}
                        onChange={(e) => setSpecialDescription(e.target.value)}
                        fullWidth
                        placeholder="Reason for special hours (e.g. Holiday Eve)"
                      />
                    </Box>
                  </Stack>
                  
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Box width={{ xs: '100%', sm: '30%' }}>
                      <ClockTimeField
                        label="Opening Time"
                        value={specialOpenTime}
                        onChange={(time) => setSpecialOpenTime(time ?? '')}
                      />
                    </Box>
                    
                    <Box width={{ xs: '100%', sm: '30%' }}>
                      <ClockTimeField
                        label="Closing Time"
                        value={specialCloseTime}
                        onChange={(time) => setSpecialCloseTime(time ?? '')}
                      />
                    </Box>
                    
                    <Box width={{ xs: '100%', sm: '40%' }} display="flex" alignItems="flex-start">
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAddSpecialHours}
                        disabled={!specialDate || !specialDescription}
                        fullWidth
                        sx={{ height: '56px' }}
                      >
                        Add Special Hours
                      </Button>
                    </Box>
                  </Stack>
                </Stack>
              </LocalizationProvider>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                Special Hours Schedule
              </Typography>
              
              {settings.specialHours.length === 0 ? (
                <Alert severity="info">
                  No special hours have been added yet.
                </Alert>
              ) : (
                <List>
                  {settings.specialHours.map((special, index) => (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={`${special.description} - ${format(parseISO(special.date), 'MMMM d, yyyy')}`}
                        secondary={
                          special.open && special.close 
                            ? `Hours: ${formatClockTime(special.open)} – ${formatClockTime(special.close)}`
                            : 'Closed'
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          aria-label="delete"
                          onClick={() => handleDeleteSpecialHours(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          {/* Payments Tab */}
          {selectedTab === 4 && (
            <Box p={3}>
              <Typography variant="h6" gutterBottom>
                Square
              </Typography>

              {squareStatus?.allowed === false ? (
                <Alert severity="warning">
                  Square is turned off for this shop by the platform administrator. Your
                  connection is kept and works again as soon as it is turned back on.
                </Alert>
              ) : squareStatus?.configured === false ? (
                <Alert severity="info">
                  Payments are not configured on this server. Set the SQUARE_*
                  environment variables on the backend to enable card payments.
                </Alert>
              ) : !squareStatus ? (
                <Typography variant="body2" color="text.secondary">
                  Loading connection status…
                </Typography>
              ) : !squareStatus.connected ? (
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={2} alignItems="flex-start">
                      {squareStatus.revoked && (
                        <Alert severity="warning">
                          This shop's Square access was revoked. Reconnect to
                          resume card payments.
                        </Alert>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        Connect this shop's Square account to take card payments
                        online and on a Square Terminal. You'll be sent to
                        Square to approve access.
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={handleSquareConnect}
                        disabled={squareBusy}
                      >
                        Connect Square
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              ) : (
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={2.5}>
                      <Box>
                        <Typography variant="subtitle2">
                          Connected to Square
                          {squareStatus.environment === 'sandbox' ? ' (sandbox)' : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Merchant {squareStatus.merchantId}
                        </Typography>
                      </Box>

                      {squareStatus.needsLocation && (
                        <Alert severity="warning">
                          Choose a location below — payments can't be taken
                          until one is selected.
                        </Alert>
                      )}

                      <FormControl sx={{ maxWidth: 360 }}>
                        <InputLabel id="square-location-label">Location</InputLabel>
                        <Select
                          labelId="square-location-label"
                          label="Location"
                          value={
                            squareLocations.some((l) => l.id === squareStatus.locationId)
                              ? squareStatus.locationId ?? ''
                              : ''
                          }
                          onChange={(e) => handleSquareLocationSave(e.target.value)}
                          disabled={squareBusy}
                        >
                          {squareLocations.map((location) => (
                            <MenuItem key={location.id} value={location.id}>
                              {location.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <TextField
                        label="Terminal device ID (optional)"
                        helperText="From Square: the paired Terminal this shop charges on. Leave empty if you only take online payments."
                        value={squareDeviceId}
                        onChange={(e) => setSquareDeviceId(e.target.value)}
                        onBlur={() => {
                          if (
                            squareStatus.locationId &&
                            (squareDeviceId.trim() || '') !== (squareStatus.deviceId || '')
                          ) {
                            handleSquareLocationSave(squareStatus.locationId);
                          }
                        }}
                        disabled={squareBusy}
                        sx={{ maxWidth: 360 }}
                      />

                      <Box>
                        <Button
                          color="error"
                          onClick={handleSquareDisconnect}
                          disabled={squareBusy}
                        >
                          Disconnect Square
                        </Button>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </Paper>
      </Container>
      
      {/* Success and error notifications */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert 
          onClose={() => setError(null)} 
          severity={error && error.includes('API permission issue') ? "warning" : "error"}
          action={
            error && error.includes('API permission issue') && (
              <Button 
                color="inherit" 
                size="small"
                onClick={() => {
                  console.log('Forcing page reload to try refreshing auth token');
                  window.location.reload();
                }}
              >
                Reload
              </Button>
            )
          }
        >
          <React.Fragment>
            <Typography>{error}</Typography>
            {error && error.includes('API permission issue') && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                User role: {user?.role || 'Unknown'} | Using temporary settings. Some changes may not be saved.
              </Typography>
            )}
          </React.Fragment>
        </Alert>
      </Snackbar>
    </motion.div>
  );
};

export default ShopSettingsPage; 